import { createReducer, createSelector, on } from '@ngrx/store';
import {
    INVOICE_CREATION_PROCESS_STEP,
    InvoiceCreationLocale,
    InvoiceCreationProcessData,
    InvoicingType
} from 'src/app/shared/constants-and-enums/invoice-creation-process-types';
import {
    InitInvoiceCreationForm,
    InvoiceCreationProcessFailure,
    InvoiceCreationProcessSuccess,
    InvoiceCreationProcessUpsertForm,
    ResetInvoiceCreationForm,
    StartInvoiceCreationProcess
} from '../actions/invoice-creation-process.actions';
import { AppState } from '../store';
import { InvoiceCreationSummary } from '@iota/iota-management-portal-v4-client';

export interface InvoiceCreationProcessState {
    step: INVOICE_CREATION_PROCESS_STEP;

    data: InvoiceCreationProcessData;

    loading: boolean;
    error: boolean;

    latestResult: InvoiceCreationSummary;
}

export const initialState: InvoiceCreationProcessState = {
    step: undefined,
    data: undefined,
    loading: false,
    error: undefined,
    latestResult: undefined
};

export const invoiceCreationProcessReducer = createReducer(
    initialState,
    on(ResetInvoiceCreationForm, () => ({ ...initialState })),
    on(InitInvoiceCreationForm, () => ({
        ...initialState,
        step: INVOICE_CREATION_PROCESS_STEP.SETTINGS
    })),

    on(InvoiceCreationProcessUpsertForm, (state, { data }) => ({ ...state, data: { ...(state?.data ?? {}), ...data } })),

    on(StartInvoiceCreationProcess, (state) => ({ ...state, loading: true, step: INVOICE_CREATION_PROCESS_STEP.RESULT })),
    on(InvoiceCreationProcessSuccess, (state, { result }) => ({
        ...state,
        loading: false,
        latestResult: result
    })),
    on(InvoiceCreationProcessFailure, (state, { error }) => ({ ...state, loading: false, latestResult: undefined, error }))
);

export const invoiceCreationProcessState = (state: AppState) => state.invoiceCreationProcessState;

export const invoiceCreationProcessCurrentStep = createSelector(invoiceCreationProcessState, ({ step }) => step);
export const invoiceCreationProcessIsInSettingsStep = createSelector(
    invoiceCreationProcessCurrentStep,
    (step) => step === INVOICE_CREATION_PROCESS_STEP.SETTINGS
);
export const invoiceCreationProcessIsInResultStep = createSelector(
    invoiceCreationProcessCurrentStep,
    (step) => step === INVOICE_CREATION_PROCESS_STEP.RESULT
);
export const invoiceCreationProcessData = createSelector(invoiceCreationProcessState, ({ data }) => data);
export const invoiceCreationProcessSelectedBusinessPartnerType = createSelector(
    invoiceCreationProcessData,
    (data) => data?.businessPartnerType
);
export const invoiceCreationProcessSelectedBusinessPartners = createSelector(
    invoiceCreationProcessData,
    (data) => data?.businessPartnerIds ?? []
);
export const invoiceCreationProcessSelectedInvoicingType = createSelector(invoiceCreationProcessData, (data) => data?.invoicingType);
export const invoiceCreationProcessSelectedLocale = createSelector(invoiceCreationProcessData, (data) => data?.locale);
export const invoiceCreationProcessDataValid = createSelector(invoiceCreationProcessData, (data) => {
    const companiesValid = data?.businessPartnerIds?.length > 0;
    const invoicingTypeValid = Object.values(InvoicingType).includes(data?.invoicingType);
    const localeValid = Object.values(InvoiceCreationLocale).includes(data?.locale);

    return companiesValid && invoicingTypeValid && localeValid;
});

export const invoiceCreationProcessLoading = createSelector(invoiceCreationProcessState, ({ loading }) => loading);
export const invoiceCreationProcessError = createSelector(invoiceCreationProcessState, ({ error }) => error);
export const invoiceCreationProcessLatestResult = createSelector(invoiceCreationProcessState, ({ latestResult }) => latestResult);
